import {
  Box,
  Stack,
  CircularProgress,
  useTheme,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { NwButton } from "@nawy/ui";
import { Mention, MentionsInput } from "react-mentions";
import { useCallback, useRef } from "react";
import useAddComments from "@/modules/lead-page/hooks/comments/add-comments.hook";
import { getMentionStyle } from "@/modules/lead-page/new/constants";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import useSanitizeMentions from "@/modules/lead-page/new/hooks/sanitize-mentions.hook";
import MentionSuggestion from "@/modules/lead-page/new/components/activity/mention-suggestion";
import TrackSelectionModal from "@/modules/lead-page/new/components/activity/track-selection-modal";
import { ArrowUp } from "lucide-react";

const AddComment = ({
  lastActivityCardStyle = false,
  addCommentButtonText = "Comment",
}: {
  lastActivityCardStyle?: boolean;
  addCommentButtonText?: string;
}) => {
  const theme = useTheme();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const { isMobile } = useScreenSize();

  const {
    commentMessage,
    mentionedUsers,
    loadAddingComment,
    notify,
    lead_type,
    setNotify,
    handleAddComments,
    handleTextareaChange,
    handleKeyPress,
    tracks,
    isLoadingTracks,
    isTracksError,
    selectedTrackUId,
    setSelectedTrackUId,
    showTrackSelectionModal,
    setShowTrackSelectionModal,
  } = useAddComments();

  const sanitizedMentionData = useSanitizeMentions({ mentionedUsers });

  const renderMentionSuggestion = useCallback(
    (
      suggestion: { id: string; display: string },
      _search: string,
      _highlightedDisplay: React.ReactNode,
      _index: number,
      focused: boolean
    ) => {
      return (
        <MentionSuggestion
          mentionedUsers={mentionedUsers}
          suggestion={suggestion}
          focused={focused}
        />
      );
    },
    [mentionedUsers]
  );

  const renderCommentInput = useCallback(() => {
    return (
      <Box
        sx={{
          flex: 1,
          overflow: "visible",
        }}
      >
        <MentionsInput
          inputRef={inputRef}
          value={commentMessage}
          onChange={handleTextareaChange}
          onKeyPress={handleKeyPress}
          placeholder={
            addCommentButtonText
              ? addCommentButtonText
              : isMobile
              ? "Comment"
              : "Add new comment"
          }
          style={getMentionStyle()}
        >
          <Mention
            trigger="@"
            data={sanitizedMentionData}
            displayTransform={(_, display) => `@${display}`}
            renderSuggestion={renderMentionSuggestion}
            style={{
              backgroundColor: "#e5f3ff",
              fontWeight: 500,
              borderRadius: 4,
              padding: `0 ${theme.nawy.spacing.xs}`,
            }}
          />
        </MentionsInput>
      </Box>
    );
  }, [
    commentMessage,
    handleTextareaChange,
    handleKeyPress,
    isMobile,
    renderMentionSuggestion,
    sanitizedMentionData,
    theme,
  ]);

  const renderAddCommentButton = useCallback(() => {
    const isSellerLead =
      lead_type?.toLowerCase() === "seller" ||
      lead_type?.toLowerCase() === "commercial seller";

    // Disable button if notify is checked and tracks are loading or failed
    const isTracksLoadingOrError = notify && (isLoadingTracks || isTracksError);
    const isButtonDisabled =
      loadAddingComment || !commentMessage.trim() || isTracksLoadingOrError;

    return (
      <Stack
        direction={isMobile ? "column" : "row"}
        alignItems={isMobile ? "stretch" : "center"}
        spacing={isMobile ? 0 : theme.nawy.spacing.xs}
        sx={{ justifyContent: "flex-end", flexShrink: 0 }}
      >
        {lastActivityCardStyle ? (
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: theme.nawy.colors.semantic.layer.accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: isButtonDisabled ? 0.5 : 1,
              pointerEvents: isButtonDisabled ? "none" : "auto",
              cursor: isButtonDisabled ? "not-allowed" : "pointer",
            }}
            onClick={isButtonDisabled ? undefined : handleAddComments}
          >
            {loadAddingComment ? (
              <CircularProgress size={20} />
            ) : (
              <ArrowUp
                size={16}
                color={theme.nawy.colors.semantic.text.onColor}
                strokeWidth={2}
              />
            )}
          </Box>
        ) : (
          <NwButton
            onClick={handleAddComments}
            disabled={isButtonDisabled}
            loading={loadAddingComment}
          >
            Add Comment
          </NwButton>
        )}

        {!lastActivityCardStyle && isSellerLead && (
          <FormControlLabel
            control={
              <Checkbox
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
                size={isMobile ? "small" : "medium"}
              />
            }
            label="Notify Resale Admin"
            sx={{
              "& .MuiFormControlLabel-label": {
                ...theme.nawy.typography.english.variants.body.sm.regular,
                color: theme.nawy.colors.semantic.text.secondary,
              },
            }}
          />
        )}
      </Stack>
    );
  }, [
    commentMessage,
    handleAddComments,
    isMobile,
    lead_type,
    loadAddingComment,
    notify,
    setNotify,
    theme,
    isLoadingTracks,
    isTracksError,
  ]);

  const handleTrackSelectionConfirm = useCallback(() => {
    if (selectedTrackUId) {
      setShowTrackSelectionModal(false);
      // Track is selected, user can now add comment when ready
    }
  }, [selectedTrackUId, setShowTrackSelectionModal]);

  const renderAddComment = useCallback(() => {
    const isSellerLead =
      lead_type?.toLowerCase() === "seller" ||
      lead_type?.toLowerCase() === "commercial seller";
    return (
      <>
        <Stack
          direction="row"
          alignItems="center"
          alignSelf="stretch"
          flex="1 0 0"
          spacing={lastActivityCardStyle ? 0 : theme.nawy.spacing.xs}
          padding={lastActivityCardStyle ? 0 : theme.nawy.spacing.md}
          borderTop={lastActivityCardStyle ? "none" : `1px solid #DEE2E7`}
          sx={{
            border: lastActivityCardStyle
              ? `1px solid ${theme.nawy.colors.semantic.border.primary}`
              : "none",
            padding: theme.nawy.spacing.md,
            position: "sticky",
            bottom: 0,

            background: theme.nawy.colors.semantic.layer.primary,
            zIndex: 1000,
            justifyContent: "space-between",
            ...(isMobile && {
              width: "100%",
              borderRadius: theme.nawy.radius.xl,
            }),
          }}
        >
          {renderCommentInput()}

          {renderAddCommentButton()}

          <TrackSelectionModal
            open={showTrackSelectionModal}
            onClose={() => setShowTrackSelectionModal(false)}
            tracks={tracks}
            selectedTrackUId={selectedTrackUId}
            onSelectTrack={setSelectedTrackUId}
            onConfirm={handleTrackSelectionConfirm}
            isLoading={isLoadingTracks}
          />
        </Stack>
        {lastActivityCardStyle && isSellerLead && (
          <FormControlLabel
            control={
              <Checkbox
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
                size={isMobile ? "small" : "medium"}
              />
            }
            label="Notify Resale Admin"
            sx={{
              marginTop: theme.nawy.spacing.xs,
              whiteSpace: "nowrap",
              ...theme.nawy.typography.english.variants.body.sm.regular,
              "& .MuiFormControlLabel-label": {
                ...theme.nawy.typography.english.variants.body.sm.regular,
                color: theme.nawy.colors.semantic.text.secondary,
              },
            }}
          />
        )}{" "}
      </>
    );
  }, [
    theme,
    renderAddCommentButton,
    renderCommentInput,
    isMobile,
    showTrackSelectionModal,
    tracks,
    selectedTrackUId,
    setSelectedTrackUId,
    setShowTrackSelectionModal,
    handleTrackSelectionConfirm,
    isLoadingTracks,
  ]);

  return renderAddComment();
};

export default AddComment;
